import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { enumAttribute, primitiveEvent, primitiveStyles } from '../shared/primitive-utils.js';
import { statusSeverities, statusStyles, type StatusSeverity } from '../shared/status-styles.js';

/**
 * Persistent inline feedback. Static messages are silent unless announcement is opted in.
 * @element qds-message
 * @attr {'neutral'|'info'|'success'|'warning'|'error'} severity - Visual semantic status.
 * @attr {string} heading - Optional visible message heading.
 * @attr {boolean} dismissible - Show the dismiss button.
 * @attr {'off'|'polite'|'assertive'} announcement - Live announcement policy. Default off.
 * @attr {string} dismiss-label - Accessible dismiss label. Default Dismiss message.
 * @property {string} severity - Semantic status.
 * @property {boolean} dismissible - Whether dismissal is available.
 * @property {'off'|'polite'|'assertive'} announcement - Live region policy.
 * @slot title - Optional title, replacing heading text.
 * @slot - Message body.
 * @slot actions - Links or actions for this message.
 * @csspart message - Outer message surface.
 * @csspart dismiss - Native dismiss button.
 * @fires {{ component: HTMLElement }} qds-dismiss - Cancelable dismissal request. Detail: { component }.
 */
export class QdsMessage extends HTMLElement {
  static get observedAttributes(): string[] { return ['severity', 'heading', 'dismissible', 'announcement', 'dismiss-label']; }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}${statusStyles}
      :host { display: block; }
      [part="message"] { display: flex; gap: 12px; padding: 12px 16px; border: 1px solid var(--theme-border-primary, #e4e9f2);
        border-inline-start: 4px solid var(--_status-accent); border-radius: 4px; background: var(--theme-background-secondary, #f8f9fa); }
      .content { flex: 1; min-width: 0; overflow-wrap: anywhere; }
      .title { font-weight: 600; }
      .actions { display: flex; flex-wrap: wrap; gap: 8px; }
      button { align-self: start; min-width: 44px; min-height: 44px; background: transparent; border: 0; border-radius: 4px; cursor: pointer; font-size: 22px; }
      button:hover { background: var(--theme-background-hover, rgba(0,0,0,.05)); }
    </style><div part="message"><div class="content"><div class="title"><slot name="title"><span></span></slot></div>
      <slot></slot><div class="actions"><slot name="actions"></slot></div></div>
      <button part="dismiss" type="button"><span aria-hidden="true">×</span></button></div>`;
    loadBaseCSS();
  }
  connectedCallback(): void {
    for (const prop of ['severity', 'dismissible', 'announcement']) upgradeProperty(this, prop);
    this.shadowRoot!.querySelector('button')!.addEventListener('click', this._onDismiss);
    this._sync();
  }
  disconnectedCallback(): void { this.shadowRoot!.querySelector('button')!.removeEventListener('click', this._onDismiss); }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get severity(): StatusSeverity { return enumAttribute(this, 'severity', statusSeverities, 'neutral'); }
  set severity(value: StatusSeverity) { this.setAttribute('severity', value); }
  get dismissible(): boolean { return this.hasAttribute('dismissible'); }
  set dismissible(value: boolean) { this.toggleAttribute('dismissible', !!value); }
  get announcement(): 'off' | 'polite' | 'assertive' { return enumAttribute(this, 'announcement', ['off', 'polite', 'assertive'], 'off'); }
  set announcement(value: 'off' | 'polite' | 'assertive') { this.setAttribute('announcement', value); }
  /** Dismiss if permitted by the application. Set hidden=false to show again. */
  dismiss(): boolean {
    if (!this.dismissible || this.hidden || !primitiveEvent(this, 'qds-dismiss', { component: this }, true)) return false;
    this.hidden = true;
    return true;
  }
  private _sync(): void {
    const panel = this.shadowRoot!.querySelector('[part="message"]')!;
    if (this.announcement === 'off') { panel.removeAttribute('role'); panel.removeAttribute('aria-live'); panel.removeAttribute('aria-atomic'); }
    else {
      panel.setAttribute('role', this.announcement === 'assertive' ? 'alert' : 'status');
      panel.setAttribute('aria-live', this.announcement); panel.setAttribute('aria-atomic', 'true');
    }
    this.shadowRoot!.querySelector('slot[name="title"] span')!.textContent = this.getAttribute('heading') || '';
    const button = this.shadowRoot!.querySelector('button')!;
    button.hidden = !this.dismissible;
    button.setAttribute('aria-label', this.getAttribute('dismiss-label') || 'Dismiss message');
  }
  private _onDismiss = (): void => { this.dismiss(); };
}
if (!customElements.get('qds-message')) customElements.define('qds-message', QdsMessage);
